import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  sourcesDeleteDocuments,
  sourcesGetDocument,
  sourcesNewDocument,
  sourcesUpdateDocuments,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, OnboardingStep, SearchScopeType, SourceDocument } from 'api/model'
import { COLORS } from 'app-constants'
import BulkActionBar from 'components/BulkActionBar'
import BulkActionModal from 'components/BulkActionBar/BulkActionModal'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import FtuxTooltip from 'components/FtuxTooltip'
import List, { ListRenderer } from 'components/List'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import { ReactComponent as GmailIcon } from 'components/Permissions/icons/gmail-icon.svg'
import { ReactComponent as GoogleDriveIcon } from 'components/Permissions/icons/google-drive-icon.svg'
import FloatingTagFilterPicker from 'components/Tags/FloatingTagFilterPicker'
import TagFilter from 'components/Tags/TagFilter'
import TagsAutocomplete from 'components/Tags/TagsAutocomplete'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useFtuxTooltip } from 'hooks/use-ftux'
import { useActiveOrganization } from 'hooks/use-organization'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { useFetchSourceDocuments } from 'hooks/use-source-documents'
import { useEffect, useMemo, useState } from 'react'
import useDrivePicker from 'react-google-drive-picker'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { formatTimestamp } from 'utils'
import EmptyStateSection from './EmptyStateSection'
import SearchScopeSelector from './VisibilitySelector'
import { FormattedCallbackDoc, formatGoogleDrivePickerDocuments } from './utils'
import { myTwinAtom } from 'state/my-twin'

// Client ID and API key from the Developer Console
export const CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID
export const API_KEY = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY

// used to filter out unsupported mime types
export const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.presentation',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
]

export enum BulkActionType {
  edit_visibility = 'Edit visibility',
  edit_tags = 'Edit tags',
  delete = 'Delete all',
}

const DocumentsList = ({
  sourceDocuments,
  setSourceDocuments,
  isLoadingSourceDocuments,
}: {
  sourceDocuments: SourceDocument[]
  setSourceDocuments: (sourceDocuments: SourceDocument[]) => void
  isLoadingSourceDocuments: boolean
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const user = useRecoilValue(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  // type of bulk action that is to be performed
  const [bulkActionType, setBulkActionType] = useState<BulkActionType | null>(null)
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState<boolean>(false)

  // used in the bulk action modal for editing visibility
  const [searchScope, setSearchScope] = useState<SearchScopeType>(SearchScopeType.global)

  // used to keep track of which documents we're applying a bulk action to
  const [checkedDocumentIds, setCheckedDocumentIds] = useState<string[]>([])
  const onItemChecked = (id: string) => {
    setCheckedDocumentIds(() => {
      if (checkedDocumentIds.includes(id)) {
        return checkedDocumentIds.filter((i) => i !== id)
      }

      return [...checkedDocumentIds, id]
    })
  }

  // used to keep track of the tags state when editing tags
  const [tags, setTags] = useState<string[]>([])
  const onTagsChange = async (value: string[]) => {
    setTags(value)
  }

  useEffect(() => {
    // whenever selected documents change, update the tags
    // get the unique list of tags from all documents
    const allTags = sourceDocuments
      .filter((doc) => checkedDocumentIds.includes(doc.id))
      .map((doc) => doc.tags ?? [])
      .flat()

    const uniqueTags = [...new Set(allTags)]
    setTags(uniqueTags)
  }, [checkedDocumentIds, sourceDocuments])

  // called when the user clicks on the submit button in the bulk action modal when editing tags
  const onSubmitScope = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      // add currently selected tags to all selected documents
      const updatedDocuments = sourceDocuments
        .filter((doc) => checkedDocumentIds.includes(doc.id))
        .map((doc) => {
          return {
            id: doc.id,
            search_scope: searchScope,
          }
        })
      const response = await sourcesUpdateDocuments(activeOrganization, updatedDocuments)

      const updatedSourceDocuments = sourceDocuments.map((doc) => {
        const updatedDocument = response.data.find((d) => d.id === doc.id)
        if (updatedDocument) {
          return updatedDocument
        } else {
          return doc
        }
      })
      setSourceDocuments(updatedSourceDocuments)

      setToast({
        title: 'Successfully updated visibility',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to update visibility',
        severity: 'error',
      })
    }
  }

  // called when the user clicks on the submit button in the bulk action modal when editing tags
  const onSubmitTags = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      // add currently selected tags to all selected documents
      const updatedDocuments = sourceDocuments
        .filter((doc) => checkedDocumentIds.includes(doc.id))
        .map((doc) => {
          return {
            id: doc.id,
            tags,
          }
        })
      const response = await sourcesUpdateDocuments(activeOrganization, updatedDocuments)

      const updatedSourceDocuments = sourceDocuments.map((doc) => {
        const updatedDocument = response.data.find((d) => d.id === doc.id)
        if (updatedDocument) {
          return updatedDocument
        } else {
          return doc
        }
      })
      setSourceDocuments(updatedSourceDocuments)

      setToast({
        title: 'Successfully updated tags',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to update tags',
        severity: 'error',
      })
    } finally {
      setTags([])
    }
  }

  const onUpdateScopeForDocument = async (id: string, scope: SearchScopeType) => {
    if (!activeOrganization) {
      return
    }

    const updatedSourceDocument = {
      id,
      search_scope: scope,
    }

    try {
      await sourcesUpdateDocuments(activeOrganization, [updatedSourceDocument])
      setToast({
        title: 'Successfully updated visibility',
        severity: 'success',
      })

      // update recoil
      setSourceDocuments(
        sourceDocuments.map((doc) => {
          if (doc.id === id) {
            return {
              ...doc,
              search_scope: scope,
            }
          }
          return doc
        })
      )
    } catch (err) {
      setToast({
        title: 'Failed to update visibility',
        severity: 'error',
      })
    }
  }

  type GetListRendererOptions = {
    hiddenColumnIndices?: number[]
    onFetchDocumentContent?: (sourceDocumentId: string) => void
  }

  const getListRenderer = (
    items: SourceDocument[],
    isCheckboxEnabled: boolean,
    isActionDisabled: boolean,
    options: GetListRendererOptions
  ) => {
    return {
      rows: items.map((sd) => {
        return {
          key: sd.id,
          columnWidth: (column) => {
            if (options.hiddenColumnIndices?.includes(column)) {
              return '0px'
            }
            switch (column) {
              case 0:
                return '20px'
              case 1:
                return '20px'
            }
          },
          columnEntry: (column) => {
            if (options.hiddenColumnIndices?.includes(column)) {
              return null
            }
            switch (column) {
              case 0:
                return (
                  <>
                    {isCheckboxEnabled && (
                      <Checkbox
                        disableRipple
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                          onItemChecked(sd.id)
                        }}
                        color="primary"
                        checked={checkedDocumentIds.includes(sd.id)}
                        sx={{
                          p: 0,
                          ml: '12px',
                        }}
                      />
                    )}
                  </>
                )
              case 1:
                return (
                  <img
                    src={
                      sd.icon_url
                        ? sd.icon_url
                        : `https://drive-thirdparty.googleusercontent.com/16/type/${sd.mime_type}`
                    }
                    alt="icon"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '8px',
                      flex: '0 0 20px',
                      marginTop: '4px',
                    }}
                  />
                )
              case 2:
                return (
                  <Link
                    href={sd.url}
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      color: COLORS.GRAY_0,
                    }}>
                    <Typography variant="pS">{sd.name}</Typography>
                  </Link>
                )
              case 3:
                return (
                  <>
                    {sd.date_indexed ? (
                      <Typography variant="pS">{formatTimestamp(sd.date_indexed)}</Typography>
                    ) : (
                      <Typography
                        variant="pS"
                        sx={{
                          whiteSpace: 'nowrap',
                        }}>
                        In progress
                      </Typography>
                    )}
                  </>
                )
              case 4:
                return (
                  <SearchScopeSelector
                    disabled={isActionDisabled}
                    searchScope={sd.search_scope}
                    setSearchScope={async (scope) => {
                      await onUpdateScopeForDocument(sd.id, scope)
                    }}
                  />
                )
              case 5:
                const icon =
                  sd.type === 'gdoc' ? (
                    <GoogleDriveIcon
                      style={{
                        width: '20px',
                        height: '20px',
                        marginTop: '4px',
                      }}
                    />
                  ) : (
                    <GmailIcon
                      style={{
                        width: '20px',
                        height: '20px',
                        marginTop: '4px',
                      }}
                    />
                  )
                return icon
              case 6:
                return (
                  <Tooltip title={sd.tags?.join(', ')} enterTouchDelay={0}>
                    <Typography
                      variant="pS"
                      flex="0 0 120px"
                      mr={1}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {(sd.tags ?? []).length > 0 ? sd.tags?.join(', ') : 'No tag'}
                    </Typography>
                  </Tooltip>
                )
              case 7:
                return options.onFetchDocumentContent && sd.date_indexed ? (
                  <Button
                    onClick={() => {
                      onFetchDocumentContent(sd.id)
                    }}>
                    Fetch content
                  </Button>
                ) : null
              default:
                return null
            }
          },
        }
      }),
      columnCount: 8,
      columnName: (column) => {
        if (options.hiddenColumnIndices?.includes(column)) {
          return null
        }
        switch (column) {
          case 0:
            return ''
          case 1:
            return ''
          case 2:
            return 'Name'
          case 3:
            return 'Last indexed'
          case 4:
            return (
              <Stack alignItems="center" direction="row" spacing="4px">
                <Typography variant="h6">Visibility</Typography>

                <Tooltip
                  enterTouchDelay={0}
                  title={
                    'Documents marked as private will not be used to generate twin responses in interactions with other users.'
                  }>
                  <InfoOutlined
                    sx={{
                      height: '16px',
                      width: '16px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </Tooltip>
              </Stack>
            )
          case 5:
            return (
              <Stack alignItems="center" direction="row" spacing="4px">
                <Typography variant="h6">Location</Typography>

                <Tooltip
                  enterTouchDelay={0}
                  title={'Documents may either be from a Gmail attachment or uploaded via Google Drive.'}>
                  <InfoOutlined
                    sx={{
                      height: '16px',
                      width: '16px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </Tooltip>
              </Stack>
            )
          case 6:
            return 'Tags'
          case 7:
            return options.onFetchDocumentContent ? 'Content' : null
          default:
            return null
        }
      },
    } as ListRenderer
  }

  const updateBulkActionType = (type: BulkActionType) => {
    setBulkActionType(type)
    setIsBulkActionModalOpen(true)
  }

  const onDismiss = () => {
    setCheckedDocumentIds([])
  }

  const onCloseBulkActionModal = () => {
    setIsBulkActionModalOpen(false)
  }

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const onBulkActionSubmit = async () => {
    if (!activeOrganization) {
      return
    }

    setIsSubmitting(true)

    switch (bulkActionType) {
      case BulkActionType.edit_visibility:
        try {
          await onSubmitScope()
          setCheckedDocumentIds([])
          setIsBulkActionModalOpen(false)
        } catch (e) {
          setToast({
            title: 'Failed to update visibility',
            severity: 'error',
          })
        }
        break
      case BulkActionType.edit_tags:
        await onSubmitTags()
        setCheckedDocumentIds([])
        setIsBulkActionModalOpen(false)
        break
      case BulkActionType.delete:
        try {
          await sourcesDeleteDocuments(activeOrganization, checkedDocumentIds)

          setToast({
            title: 'Successfully deleted documents',
            severity: 'success',
          })
          // update recoil state
          setSourceDocuments(sourceDocuments.filter((sd) => !checkedDocumentIds.includes(sd.id)))
          setCheckedDocumentIds([])
          setIsBulkActionModalOpen(false)
        } catch (e) {
          setToast({
            title: 'Failed to delete documents',
            severity: 'error',
          })
        }
        break
      default:
        break
    }

    setIsSubmitting(false)
  }

  // for Jelled debugging purposes
  const [isDocumentContentDialogOpen, setIsDocumentContentDialogOpen] = useState<boolean>(false)
  const [documentWithContent, setDocumentWithContent] = useState<SourceDocument | null>(null)
  const onFetchDocumentContent = async (sourceDocumentId: string) => {
    if (!activeOrganization || !sourceDocumentId) {
      return
    }

    try {
      const response = await sourcesGetDocument(activeOrganization, sourceDocumentId)
      if (response.data.content) {
        setDocumentWithContent(response.data)
        setIsDocumentContentDialogOpen(true)
      }
    } catch (e) {
      setToast({
        title: 'Failed to fetch document content',
        severity: 'error',
      })
    }
  }

  const renderBulkActionModal = () => {
    switch (bulkActionType) {
      case BulkActionType.edit_visibility:
        return (
          <BulkActionModal
            isSubmitting={isSubmitting}
            isOpen={isBulkActionModalOpen}
            onSubmitClick={onBulkActionSubmit}
            submitButtonText="Save"
            headerText="Edit visibility"
            content={
              <Stack>
                <SearchScopeSelector
                  disabled={false}
                  entityName="Documents"
                  searchScope={searchScope}
                  setSearchScope={setSearchScope}
                />

                <Typography variant="pXS" mt={2} mb={4}>
                  Visibility settings will be applied to the following documents below.
                </Typography>

                <List
                  renderer={getListRenderer(
                    sourceDocuments.filter((sd) => checkedDocumentIds.includes(sd.id)),
                    false,
                    true,
                    {
                      hiddenColumnIndices: [3],
                    }
                  )}
                  isLoading={isLoadingSourceDocuments}
                />
              </Stack>
            }
            onClose={onCloseBulkActionModal}
          />
        )
      case BulkActionType.edit_tags:
        return (
          <BulkActionModal
            isSubmitting={isSubmitting}
            isOpen={isBulkActionModalOpen}
            onSubmitClick={onBulkActionSubmit}
            submitButtonText="Save"
            onClose={onCloseBulkActionModal}
            headerText="Edit tags"
            content={
              <Stack>
                <TagsAutocomplete initialValue={tags} onValueChanged={onTagsChange} />
                <Typography
                  mt={1}
                  mb={4}
                  variant="pXS"
                  sx={{
                    color: COLORS.GRAY_3,
                  }}>
                  These tags will be applied to the following documents.
                </Typography>
                <List
                  renderer={getListRenderer(
                    sourceDocuments.filter((sd) => checkedDocumentIds.includes(sd.id)),
                    false,
                    true,
                    {
                      hiddenColumnIndices: [3],
                    }
                  )}
                  isLoading={isLoadingSourceDocuments}
                />
              </Stack>
            }
          />
        )
      case BulkActionType.delete:
        return (
          <BulkActionModal
            isSubmitting={isSubmitting}
            isOpen={isBulkActionModalOpen}
            onSubmitClick={onBulkActionSubmit}
            submitButtonText="Delete"
            headerText="Are you sure you want to delete the following documents?"
            onClose={onCloseBulkActionModal}
            content={
              <Stack>
                <List
                  renderer={getListRenderer(
                    sourceDocuments.filter((sd) => checkedDocumentIds.includes(sd.id)),
                    false,
                    true,
                    {
                      hiddenColumnIndices: [3],
                    }
                  )}
                  isLoading={isLoadingSourceDocuments}
                />
              </Stack>
            }
          />
        )
      default:
        return null
    }
  }

  return (
    <Stack mt={2} spacing={2}>
      <BulkActionBar
        isOpen={checkedDocumentIds.length > 0}
        buttons={[
          <Button
            size="medium"
            variant="outlined"
            onClick={() => {
              updateBulkActionType(BulkActionType.edit_visibility)
            }}
            sx={{
              color: COLORS.GRAY_0,
              background: COLORS.PURE_WHITE,
              border: `1px solid ${COLORS.GRAY_5}`,
              '&:hover': {
                borderWidth: '1px',
              },
            }}>
            Edit visibility
          </Button>,

          <Button
            size="medium"
            variant="outlined"
            onClick={() => {
              updateBulkActionType(BulkActionType.edit_tags)
            }}
            sx={{
              whiteSpace: 'nowrap',
              color: COLORS.GRAY_0,
              background: COLORS.PURE_WHITE,
              border: `1px solid ${COLORS.GRAY_5}`,
              '&:hover': {
                borderWidth: '1px',
              },
            }}>
            Edit tags
          </Button>,

          <Button
            variant="outlined"
            size="medium"
            onClick={() => {
              updateBulkActionType(BulkActionType.delete)
            }}
            sx={{
              whiteSpace: 'nowrap',
              color: COLORS.GRAY_0,
              background: COLORS.PURE_WHITE,
              border: `1px solid ${COLORS.GRAY_5}`,
              '&:hover': {
                borderWidth: '1px',
              },
            }}>
            Delete
          </Button>,
        ]}
        onDismiss={onDismiss}
      />
      <List
        renderer={getListRenderer(sourceDocuments, true, false, {
          onFetchDocumentContent: user?.is_system_admin ? onFetchDocumentContent : undefined,
        })}
        isLoading={isLoadingSourceDocuments}
      />
      {renderBulkActionModal()}
      {documentWithContent && (
        <Dialog open={isDocumentContentDialogOpen}>
          <DialogTitle>Document contents</DialogTitle>
          <DialogContent>
            <Stack mt={2}>
              <Typography variant="pSB">Document ID:</Typography>
              <Typography variant="pS">{documentWithContent.id}</Typography>

              <Typography mt={2} variant="pSB">
                Twin ID:
              </Typography>
              <Typography variant="pS">{myTwin?.id}</Typography>

              <Typography variant="pSB" mt={2}>
                Document content:
              </Typography>
              <Typography
                sx={{
                  whiteSpace: 'pre-line',
                }}>
                {documentWithContent.content}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsDocumentContentDialogOpen(false)
                setDocumentWithContent(null)
              }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  )
}

const MyTwinFiles = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()
  const setToast = useSetRecoilState(toastAtom)

  const [user] = useRecoilState(authenticatedUserAtom)
  const hasSetTwinProfile = !!user?.onboarding_steps_completed.includes(OnboardingStep.set_twin_profile)

  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagPickerAnchorEl, setTagPickerAnchorEl] = useState<null | HTMLElement>(null)

  // used to trigger a source documents refresh when a document is added, this is to ensure that we show the indexing time which happens after a period of time
  const [shouldRefetchSourceDocuments, setShouldRefetchSourceDocuments] = useState<boolean>(false)

  const {
    sourceDocuments,
    isLoading: isLoadingSourceDocuments,
    setSourceDocuments,
    refetchSourceDocuments,
  } = useFetchSourceDocuments(selectedTags)

  const [openPicker, authResult] = useDrivePicker()

  // used to show an alert that displays a list of unsupported documents that were not uploaded
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false)
  const [supportedDocs, setSupportedDocs] = useState<FormattedCallbackDoc[]>([])
  const [unsupportedDocs, setUnsupportedDocs] = useState<FormattedCallbackDoc[]>([])

  // permissions state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }
  const { featurePermissions } = useFetchCheckPermissions(Feature.google_drive)
  const hasPermissions = featurePermissions?.isPermitted ?? false
  const acceptedPermissions = featurePermissions?.acceptedPermissions ?? []
  const missingPermissions = featurePermissions?.missingPermissions ?? []

  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // store access_token into local storage
  useEffect(() => {
    if (authResult?.access_token) {
      localStorage.setItem('jelled_google-drive-access-token', JSON.stringify(authResult?.access_token))
    } else {
      // if a token is expired, we want to remove it
      localStorage.removeItem('jelled_google-drive-access-token')
    }
  }, [authResult])

  useEffect(() => {
    if (shouldRefetchSourceDocuments) {
      // refetch notes
      window.setTimeout(() => {
        refetchSourceDocuments()
        setShouldRefetchSourceDocuments(false)
      }, 5000)
    }
  }, [shouldRefetchSourceDocuments, refetchSourceDocuments])

  const handleOpenPicker = () => {
    const getAuthToken = () => {
      return localStorage.getItem('jelled_google-drive-access-token')
        ? JSON.parse(localStorage.getItem('jelled_google-drive-access-token') || '')
        : ''
    }

    openPicker({
      clientId: CLIENT_ID ?? '',
      developerKey: API_KEY ?? '',
      token: getAuthToken(),
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      customScopes: ['https://www.googleapis.com/auth/drive.readonly'],
      // customViews: customViewsArray, // custom view
      callbackFunction: async (data) => {
        if (!activeOrganization) {
          return
        }

        if (data.action === 'cancel') {
          return
        }

        if (data.docs) {
          const formattedDocs = formatGoogleDrivePickerDocuments(data.docs)

          // filter out all duplicate docs / docs that have an unsupported mime type
          const filteredUniqueDocs = formattedDocs.filter((doc) => {
            return !sourceDocuments.find((d) => d.document_id === doc.document_id)
          })

          const filteredDocsBySupportedMimeType = formattedDocs.filter((doc) => {
            return SUPPORTED_MIME_TYPES.includes(doc.mime_type)
          })

          const unsupportedDocs = formattedDocs.filter((doc) => {
            return !SUPPORTED_MIME_TYPES.includes(doc.mime_type)
          })

          const someDocsAreDuplicates = formattedDocs.length !== filteredUniqueDocs.length
          const someDocsAreUnsupported = formattedDocs.length !== filteredDocsBySupportedMimeType.length

          try {
            setIsSubmitting(true)
            const response = await sourcesNewDocument(activeOrganization, filteredDocsBySupportedMimeType)

            // make sure to only add the docs that have not yet already been added to the source documents
            const addedDocs = response.data.filter((doc) => {
              return !sourceDocuments.find((d) => d.document_id === doc.document_id)
            })

            setSourceDocuments([...sourceDocuments, ...addedDocs])

            if (filteredDocsBySupportedMimeType.length > 0) {
              if (someDocsAreUnsupported) {
                setToast({
                  title: 'Some documents were not added because they are not supported',
                  severity: 'warning',
                })
              } else if (someDocsAreDuplicates) {
                setToast({
                  title: 'Some documents were updated',
                  severity: 'success',
                })
              } else {
                setToast({
                  title: 'All documents were added',
                  severity: 'success',
                })
              }

              // we should update floating checklist button if the user has uploaded any new documents
              if (!hasSetTwinProfile) {
                updateOnboardingStep()
              }
            } else {
              setToast({
                title: 'Failed to upload documents as there are some duplicates or unsupported mime types',
                severity: 'error',
              })
              setShouldRefetchSourceDocuments(true)
            }

            // show alert only if there are any documents that were unsupported
            if (unsupportedDocs.length !== 0) {
              setSupportedDocs(supportedDocs)
              setUnsupportedDocs(unsupportedDocs)
              setIsAlertVisible(true)
            }
          } catch {
            setToast({
              title: 'Failed to add documents',
              severity: 'error',
            })
          } finally {
            setIsSubmitting(false)
          }
        }
      },
    })
  }

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredDocumentsByName = useMemo(() => {
    return sourceDocuments.filter((n) => {
      return n.name.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [sourceDocuments, searchText])

  const filteredDocumentsByTag = useMemo(() => {
    return filteredDocumentsByName.filter((doc) => {
      if (selectedTags.length === 0) {
        return true
      }
      return doc.tags?.some((tag) => selectedTags.includes(tag))
    })
  }, [filteredDocumentsByName, selectedTags])

  const renderContent = () => {
    // if loading, show loading state
    if (isLoadingSourceDocuments) {
      return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
    }

    return (
      <Stack>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          spacing={2}
          mb={2}>
          <TextField
            placeholder="Search files"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      marginRight: '-10px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            style={{
              width: isMobile ? '100%' : '260px',
            }}
          />

          <FtuxTooltip
            open={isFtuxTooltipOpen}
            setOpen={setIsFtuxTooltipOpen}
            headerText="Add files"
            subtitleText="Increase your twin’s knowledge base by adding files for your twin to reference when answering questions."
            progressText="5 of 6"
            onBackClick={() => {
              navigate('/my-twin/calendars')
            }}
            onNextClick={() => {
              navigate('/my-twin/instructions')
            }}
            placement={isMobile ? 'bottom' : 'right-start'}
            onDismiss={() => {
              setIsFtuxTooltipOpen(false)
            }}>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              color="primary"
              startIcon={<AddOutlinedIcon />}
              onClick={onAddDocumentClicked}
              sx={{
                maxHeight: '36px',
                width: 'fit-content',
              }}>
              Add file
            </LoadingButton>
          </FtuxTooltip>
        </Stack>

        <TagFilter
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          tagPickerAnchorEl={tagPickerAnchorEl}
          setTagPickerAnchorEl={setTagPickerAnchorEl}
        />

        <FloatingTagFilterPicker
          anchorEl={tagPickerAnchorEl}
          onClose={() => setTagPickerAnchorEl(null)}
          selectedTags={selectedTags}
          onSelectTags={(tags: string[]) => {
            setSelectedTags && setSelectedTags(tags)
            localStorage.setItem('jelled_selectedTags', JSON.stringify(tags))
          }}
        />

        {isAlertVisible && (
          <Alert
            severity="warning"
            onClose={() => setIsAlertVisible(false)}
            style={{
              marginTop: '16px',
              marginBottom: '16px',
            }}>
            <AlertTitle>Some files were not uploaded due to having an unsupported file type</AlertTitle>
            We currently only support the following file types:
            <ul>
              <li>Google Documents</li>
              <li>Google Sheets</li>
              <li>Google Presentations</li>
              <li>Microsoft Office - Excel spreadsheets</li>
              <li>Microsoft Office - Powerpoint slides</li>
              <li>Microsoft Office - Word documents</li>
              <li>PDFs</li>
              <li>CSVs</li>
            </ul>
            <br />
            The following files were skipped:
            <ul>
              {unsupportedDocs.map((doc) => {
                return (
                  <li>
                    {doc.name} - ({doc.mime_type})
                  </li>
                )
              })}
            </ul>
          </Alert>
        )}

        {sourceDocuments.length === 0 && <EmptyStateSection headerText="No files yet" />}

        {sourceDocuments.length > 0 && (
          <DocumentsList
            sourceDocuments={filteredDocumentsByTag}
            setSourceDocuments={setSourceDocuments}
            isLoadingSourceDocuments={isLoadingSourceDocuments}
          />
        )}
      </Stack>
    )
  }

  const onAddDocumentClicked = () => {
    // if no permissions yet, then we should show the permissions modal
    if (!hasPermissions) {
      setIsPermissionsModalOpen(true)
      return
    }

    // otherwise, show the picker
    handleOpenPicker()
  }

  return (
    <Stack
      borderLeft="1px solid #DDDDDD"
      width="100%"
      height="100vh"
      sx={{
        p: 3,
        background: COLORS.GRAY_8,
      }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        maxWidth="1024px">
        <Stack pr={8}>
          {!isMobile && (
            <Typography variant="h4" mb={2}>
              Files
            </Typography>
          )}

          <Typography
            variant="pS"
            mb={2}
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Add files from Google Drive so that your twin can use additional context to answer questions. Attached files
            in Gmail are automatically added and their visibility is set to private by default.
          </Typography>
        </Stack>
      </Stack>

      {renderContent()}

      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.google_drive}
        />
      )}
    </Stack>
  )
}

export default withOnboardingStep(MyTwinFiles, OnboardingStep.set_twin_profile)
